import { useMemo, useState } from 'react';
import { useGetModificationGroups } from './api/useGetModificationGroups';
import { useI18n } from '~Hooks';
import { ModificationGroupFragment_variations } from './api/__queries__';
import { MODIFICATION_GROUP_TYPE } from '~../__queries__/globalTypes';
import { useInventoryHelpers } from '~Components/InventoryUniverse';
import { formatVariations, getInitiallySelectedVariations } from './utils';
import { LocalVariation } from '~Types';
import { useItemCategoryMap } from './api/useGetItemCategoryMap';

interface Variation {
  id: string;
  name: string;
  selected: boolean;
  price: number;
  imageUrl: string | undefined;
  asItem: {
    id: string;
  } | null;
  category: {
    id: string;
    name: string;
  } | null;
  isOutOfStock?: boolean;
}

/** This is not the raw ModificationGroup type — we add extra fields like `valid` which is calculated inside the hook */
export interface FormattedModificationGroup {
  id: string;

  /** The external name of the modification group */
  title: string;

  /** The type of modification group (SINGLE or MULTIPLE) */
  type: MODIFICATION_GROUP_TYPE;

  /** All the variations that belong to this group */
  variations: Variation[];

  /** If this is true - At least one variation must be selected to continue in EPOS or POS */
  required: boolean;

  /** Is this group valid? If any group is invalid, we block "continue" in both EPOS & POS (e.g., required group with no selection) */
  valid: boolean;

  /** If this group is attached to an item, the ItemModifications.tsx popup will open when pressing the item in POS */
  expandedByDefault: boolean;

  /** In EPOS or POS, which variations should be preselected.
   * Only applies to SINGLE groups (one can be preselected) or MULTIPLE if it's a removable ingredient group (then all are preselected).
   */
  preselectedVariations: string[];

  /** If true, variations in this group are shown using tab layout instead of columns */
  isGridViewEnabled?: boolean;

  /** True if the group is a removable-ingredients group with all variations preselected */
  isRemovableIngredientGroup?: boolean;
}

/** If the modification group's internalName starts with this key, then it's a removable ingredient group */
export const REMOVABLE_INGREDIENTS_KEY = 'removable-ingredients';

/**
 * Helper hook to:
 * - Get an items variations & their selections
 * - Handle the selection of variations
 *
 * Info: A modification group is a wrapper group around variations to give information about how it should be displayed and handled
 * @param modificationGroupIds - Array of modification group IDs (modificationGroups is saved under Item)
 * @param prevSelectedVariationIds - Array of variation IDs that currently is selected. E.g user selects a variation, closes the popup and reopens it, these should remain. (ONLY POS)
 * @param prevRemovedVariationIds - Array of variation IDs that have been removed. E.g user removed a preselected variation, closes the popup and reopens it, these should be not be preselected. (ONLY POS)
 */
const useItemModifications = (
  modificationGroupIds: string[],
  prevSelectedVariationIds?: string[],
  prevRemovedVariationIds?: string[],
) => {
  const { i18n } = useI18n();
  const inventoryHelper = useInventoryHelpers();

  const itemCategoryMap = useItemCategoryMap();
  const modificationGroups = useGetModificationGroups(modificationGroupIds);
  const allVariations = modificationGroups.flatMap((mg) => mg.variations);

  /**
   * This is variations that should already be selected when entering the items modifications in either EPOS or POS
   * If the group is MULTIPLE (meaning multiple variations can be selected), then the preselected variations should never be a thing
   * unless the group is a removable ingredients group (but we have code for that later in the hook)
   */
  const preselectedVariations = useMemo(
    () =>
      modificationGroups
        .filter((mg) => mg.type === MODIFICATION_GROUP_TYPE.MULTIPLE)
        .flatMap((mg) =>
          mg.preselectedVariations
            .map((id) => mg.variations.find((v) => v.id === id))
            .filter((v): v is ModificationGroupFragment_variations => !!v),
        ),
    [modificationGroups],
  );
  /** We don't allow selection for those that are out of stock */
  const outOfStockPreselectedVariationIds = preselectedVariations
    .map((v) => v.id)
    .filter((vid) => inventoryHelper.itemIsOutOfStock(vid));

  /** If we have the ids passed in from POS use that, otherwise check what variations that is preselected */
  const initiallySelectedVariationIds =
    prevSelectedVariationIds && prevSelectedVariationIds.length > 0
      ? prevSelectedVariationIds
      : getInitiallySelectedVariations(modificationGroups);

  const [selectedVariationIds, setSelectedVariationIds] = useState<string[]>([
    ...initiallySelectedVariationIds,
    ...(prevRemovedVariationIds || []), // If these have been removed before these works as inverting the preselected
    ...outOfStockPreselectedVariationIds,
  ]);

  /* Check if a variation is configured as a preselected somewhere in any of the groups that has been passed in the hook */
  const isPreselectedInAny = (id: string) => preselectedVariations.some((dv) => dv.id === id);

  /* Check if this is currently selected */
  const isSelected = (id: string) => selectedVariationIds.includes(id);

  /** Runs when a variation has been touched in EPOS or POS */
  const onSelectVariation = (variationId: string) => {
    const isThisVariationSelected = isSelected(variationId);

    /* Find the group that the variation belongs to */
    const group = modificationGroups.find((mg) => mg.variations.some((v) => v.id === variationId));

    /* If it's a single group and we are clicking an option that was already selected, do nothing. */
    if (isThisVariationSelected && group?.type === MODIFICATION_GROUP_TYPE.SINGLE) {
      return;
    }

    /* If it's a multi group and it's already selected, then unmark it as selected*/
    if (isThisVariationSelected) {
      setSelectedVariationIds(selectedVariationIds.filter((svID) => svID !== variationId));
      return;
    }

    /* If it's not selected and it's a single group, we need to first remove the other selected before selecting since only one can be selected in this group */
    if (group?.type === MODIFICATION_GROUP_TYPE.SINGLE) {
      /* We keep the existing selections that are not part of this group.
       * This is important since some of them may be out-of-stock variations, which are not selectable but still present.
       */
      const idsInGroup = group?.variations.map((vari) => vari.id) || [];
      const keepSelections = selectedVariationIds.filter((svID) => !idsInGroup.includes(svID));

      setSelectedVariationIds([...keepSelections, variationId]);
      return;
    }

    /* If its not selected & MULTIPLE - Just add selection to the group */
    setSelectedVariationIds([...selectedVariationIds, variationId]);
  };

  /* Format our state to something that is easily consumable by the client */
  const formattedModificationGroups = useMemo(
    () =>
      modificationGroups.map((mg) => {
        let atLeastOneSelected = false;

        const formattedVariations = formatVariations({
          variations: mg.variations,
          isSelected,
          isPreselectedInAny,
          itemCategoryMap,
          itemIsOutOfStock: inventoryHelper.itemIsOutOfStock,
          i18n,
        }).map((v) => {
          if (v.selected) {
            atLeastOneSelected = true;
          }
          return v;
        });

        return {
          id: mg.id,
          title: i18n(mg.externalName),
          variations: formattedVariations,
          required: mg.required,
          expandedByDefault: mg.expandedByDefault,
          type: mg.type,
          valid: !mg.required || atLeastOneSelected,
          preselectedVariations: mg.preselectedVariations || [],
          isGridViewEnabled: mg.isGridViewEnabled,
          isRemovableIngredientGroup: mg.internalName.startsWith(REMOVABLE_INGREDIENTS_KEY),
        };
      }),
    [selectedVariationIds, itemCategoryMap, inventoryHelper.itemIsOutOfStock],
  );

  /* Map the selected variations & exclude the preselected ones */
  const formattedSelectedVariations = selectedVariationIds
    .filter((variId) =>
      allVariations.some(
        (vari) => vari.id === variId && !preselectedVariations.some((pv) => pv.id === variId),
      ),
    )
    .map((id) => {
      const variation = allVariations.find((v) => v.id === id)!;

      return {
        id: variation.id,
        name: i18n(variation.nameLang),
        price: variation.price,
        asItem: variation.asItem
          ? {
              id: variation.asItem.id,
              categories: [itemCategoryMap[variation.asItem.id]],
            }
          : undefined,
      };
    });

  /* Find all removed variations and convert them to local (i18n) format */
  const removedVariations: LocalVariation[] = useMemo(
    () => [
      ...preselectedVariations
        .filter((defVari) => selectedVariationIds.some((id) => id === defVari.id))
        .map((rv) => ({
          ...rv,
          name: i18n(rv.nameLang),
        })),
    ],
    [selectedVariationIds],
  );

  return {
    onSelectVariation,
    modificationGroups: formattedModificationGroups,
    selectedVariations: formattedSelectedVariations,
    removedVariations,
  };
};

export default useItemModifications;
